'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  Sparkles,
  CheckCircle2,
  X,
} from 'lucide-react';

interface FeatureCard {
  id: string;
  badge: string;
  badgeColor: string;
  title: string;
  desc: string;
  outputSnippet: string;
  outputDetails?: string[];
}

export const TTOSFeaturesV2: React.FC = () => {
  const [selectedFeature, setSelectedFeature] = useState<FeatureCard | null>(null);

  const features: FeatureCard[] = [
    {
      id: 'manager',
      badge: 'AI Business Manager',
      badgeColor: 'bg-purple-100 text-purple-700 border-purple-200',
      title: 'AI Business Manager',
      desc: 'Your personal AI assistant that understands your business, industry and goals.',
      outputSnippet: 'Here are 5 actions to get more customers this week...',
      outputDetails: [
        'Run 15% weekend combo promotion on Swiggy & Zomato',
        'Auto-post 3 reels during peak dining hours (7:30 PM)',
        'Broadcast Google Review reminder to 150 happy customers',
      ],
    },
    {
      id: 'website',
      badge: 'Website',
      badgeColor: 'bg-blue-100 text-blue-700 border-blue-200',
      title: 'AI Website Builder',
      desc: 'Create professional websites with industry-specific templates and AI content.',
      outputSnippet: 'Modern business website with 5 pages, ready to publish',
      outputDetails: [
        'Home, Services, About Us, Case Studies & Contact Forms',
        '100% Mobile Responsive with Instant WhatsApp Chat button',
        'Google Analytics & Meta Pixel pre-integrated',
      ],
    },
    {
      id: 'marketing',
      badge: 'Marketing',
      badgeColor: 'bg-orange-100 text-orange-700 border-orange-200',
      title: 'Marketing Automation',
      desc: 'Automate your campaigns, follow-ups and customer journeys.',
      outputSnippet: 'Email campaign with 3 follow-ups and 40% higher conversion rate',
      outputDetails: [
        'Day 0: Personalized Welcome + Catalog PDF',
        'Day 2: Customer Case Study & Social Proof',
        'Day 5: Limited-Time 20% Discount Code with Countdown',
      ],
    },
    {
      id: 'social',
      badge: 'Social Media',
      badgeColor: 'bg-pink-100 text-pink-700 border-pink-200',
      title: 'Social Media Manager',
      desc: 'Plan, create, schedule and analyze across all your social channels.',
      outputSnippet: '5 branded posts + captions + relevant hashtags',
      outputDetails: [
        'Instagram Carousel + Reels Script + Audio recommendations',
        'LinkedIn thought leadership carousel with slide previews',
        'X (Twitter) thread with 7 high-engagement tweets',
      ],
    },
    {
      id: 'seo',
      badge: 'SEO',
      badgeColor: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      title: 'SEO & Keyword Research',
      desc: 'Find high-value keywords and optimize your content for search engines.',
      outputSnippet: 'Top 30 keywords with search volume, competition and opportunity score',
      outputDetails: [
        'Top 10 High-Intent Local Keywords with Volume > 12,000/mo',
        'Competitor backlink gap and on-page schema suggestions',
        'Google My Business local 3-pack optimization checklist',
      ],
    },
    {
      id: 'content',
      badge: 'Content',
      badgeColor: 'bg-purple-100 text-purple-700 border-purple-200',
      title: 'AI Blog & Content Studio',
      desc: 'Generate blogs, articles, website copy and marketing content.',
      outputSnippet: 'SEO-optimized blog with title, outline and full content',
      outputDetails: [
        '2,200-word comprehensive industry guide with FAQs',
        'Includes Meta Title, Description and Schema markup',
        'Zero AI detection score with authentic conversational tone',
      ],
    },
    {
      id: 'images',
      badge: 'AI Images',
      badgeColor: 'bg-rose-100 text-rose-700 border-rose-200',
      title: 'AI Image Generator',
      desc: 'Create stunning branded images for your social media, ads and website.',
      outputSnippet: 'Instagram post image with your logo and brand colors',
      outputDetails: [
        '1080x1080 High-resolution graphics matching brand palette',
        'Text overlay generator with auto font hierarchy',
        'Banner formats for Google Ads, Facebook Ads & YouTube Thumbnails',
      ],
    },
    {
      id: 'videos',
      badge: 'AI Videos',
      badgeColor: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      title: 'AI Video Creator',
      desc: 'Turn your ideas into engaging videos with AI and ready-to-use templates.',
      outputSnippet: '30 sec promotional video with voiceover and captions',
      outputDetails: [
        '9:16 vertical short format for Instagram Reels & YouTube Shorts',
        'Realistic natural Indian accent voiceovers (Hindi & English)',
        'Kinetic animated word-by-word subtitles',
      ],
    },
    {
      id: 'automation',
      badge: 'Automation',
      badgeColor: 'bg-violet-100 text-violet-700 border-violet-200',
      title: 'Workflow Automation',
      desc: 'Save time with automated workflows and smart triggers.',
      outputSnippet: 'New lead → Welcome email → Whatsapp message',
      outputDetails: [
        'Instant webhook ingestion from Meta Lead Ads & Google Ads',
        'WhatsApp interactive button message with brochure attachment',
        'Automatic task assignment in team CRM',
      ],
    },
    {
      id: 'analytics',
      badge: 'Analytics',
      badgeColor: 'bg-sky-100 text-sky-700 border-sky-200',
      title: 'Business Analytics',
      desc: 'Track performance, measure growth and get AI insights.',
      outputSnippet: 'Your website traffic increased by 45% this month',
      outputDetails: [
        'Lead conversion rate up from 2.4% to 5.8%',
        'Customer acquisition cost decreased by ₹420 per lead',
        'Automated executive PDF summary delivered every Monday',
      ],
    },
    {
      id: 'hosting',
      badge: 'Hosting',
      badgeColor: 'bg-teal-100 text-teal-700 border-teal-200',
      title: 'Hosting & Servers',
      desc: 'Fast, secure and reliable hosting for your website and applications.',
      outputSnippet: '99.9% uptime, SSL secured, global CDN',
      outputDetails: [
        'Sub-100ms response time with edge servers in Mumbai & Delhi',
        'Automated daily backups and DDoS firewall protection',
        'Free custom SSL certificates and one-click DNS configuration',
      ],
    },
    {
      id: 'tools',
      badge: 'Business Tools',
      badgeColor: 'bg-amber-100 text-amber-700 border-amber-200',
      title: '100+ Business Tools',
      desc: 'From email marketing to QR codes, all the tools you need in one place.',
      outputSnippet: 'Generate QR code, shorten link, remove background and more...',
      outputDetails: [
        'Dynamic branded QR codes with scan analytics',
        'One-click AI background remover and photo enhancer',
        'GST invoice calculator and professional contract generator',
      ],
    },
  ];

  return (
    <section id="features-grid" className="py-20 bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Top Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4 pb-4 border-b border-slate-100">
          <div className="space-y-1.5 max-w-2xl">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Our Powerful Features
            </h2>
            <p className="text-sm text-slate-500">
              Everything you need, from AI-powered business management to advanced marketing tools.
            </p>
          </div>
          <div className="text-xs text-slate-400 font-medium">
            Choose a feature, explore how it works, and see what you can create.
          </div>
        </div>

        {/* 12 Features in 3x4 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((item) => (
            <div
              key={item.id}
              className="group bg-white rounded-2xl p-6 border border-slate-200/90 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between hover:border-indigo-300"
            >
              <div>
                {/* Badge Tag */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${item.badgeColor}`}>
                    {item.badge}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-base font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                  {item.desc}
                </p>

                {/* Example Output Box (matching screenshot exactly) */}
                <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
                  <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    <Sparkles className="w-3 h-3 text-indigo-500" />
                    <span>Example Output</span>
                  </div>
                  <p className="text-xs font-medium text-slate-700 font-mono italic">
                    &ldquo;{item.outputSnippet}&rdquo;
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-4 mt-4 border-t border-slate-100 flex items-center justify-between">
                <button
                  onClick={() => setSelectedFeature(item)}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 group/btn"
                >
                  <span>Learn More</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" />
                </button>
                <Link
                  href="/register"
                  className="text-[11px] font-semibold text-slate-400 hover:text-slate-700"
                >
                  Try in Sandbox
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Feature Detail Modal */}
        {selectedFeature && (
          <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full border border-slate-200 shadow-2xl space-y-5 animate-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <span className={`px-3 py-1 rounded-full text-xs font-bold border ${selectedFeature.badgeColor}`}>
                  {selectedFeature.badge}
                </span>
                <button
                  onClick={() => setSelectedFeature(null)}
                  className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                <h3 className="text-xl font-bold text-slate-900">{selectedFeature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{selectedFeature.desc}</p>
              </div>

              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                <span className="text-xs font-extrabold text-slate-700 block uppercase tracking-wide">
                  Autonomous Deliverables Generated:
                </span>
                <div className="space-y-2">
                  {selectedFeature.outputDetails?.map((detail, dIdx) => (
                    <div key={dIdx} className="flex items-start gap-2 text-xs text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span>{detail}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  onClick={() => setSelectedFeature(null)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  Close
                </button>
                <Link
                  href="/register"
                  className="px-5 py-2.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm"
                >
                  Test This Feature Free →
                </Link>
              </div>
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default TTOSFeaturesV2;
